import React, {useEffect, useMemo, useRef} from 'react';
import ReSelect, {
    components,
    createFilter,
    DropdownIndicatorProps,
    GroupBase,
    MenuListProps,
    SelectComponentsConfig,
    StylesConfig,
} from 'react-select';
import {Icon} from '@iconify/react';
import {defaultFonts} from '../../../utils/defaultFonts';
import {getCssVariable} from '../../../utils/style.utils';

export type Option = {
    value: string | string[];
    label: string;
};

interface SelectProps {
    placeholder?: string;
    options: Option[];
    value?: Option;
    onChange?: (e: Option | null) => void;
    onInputChange?: (e: string) => void;
    isMenuUsingPortal?: boolean;
    name?: string;
    disabled?: boolean;
    components?: SelectComponentsConfig<Option, boolean, GroupBase<Option>>;
    styles?: StylesConfig<Option, boolean, GroupBase<Option>>;
    dontCloseMenuOnScroll?: boolean;
    dataCy?: string;
    className?: string;
    onScrollToBottom?: () => void;
    menuPlacement?: 'auto' | 'top' | 'bottom';
}

const SCROLL_THRESHOLD = 0.8;

const customDropdownIndicator = <Option, IsMulti extends boolean = false>(
    props: DropdownIndicatorProps<Option, IsMulti, GroupBase<Option>>
): JSX.Element => (
    <components.DropdownIndicator {...props}>
        <Icon icon="solar:alt-arrow-down-linear" />
    </components.DropdownIndicator>
);

function createCustomMenuList(onScrollToBottomRef: React.MutableRefObject<(() => void) | undefined>) {
    return function CustomMenuList({innerProps, ...restProps}: MenuListProps<Option, boolean, GroupBase<Option>>): JSX.Element {
        const handleScroll = (e: React.UIEvent<HTMLDivElement>): void => {
            const {scrollTop, scrollHeight, clientHeight} = e.currentTarget;
            const scrollRatio = (scrollTop + clientHeight) / scrollHeight;
            if (scrollRatio >= SCROLL_THRESHOLD) {
                onScrollToBottomRef.current?.();
            }
        };

        return <components.MenuList {...restProps} innerProps={{...innerProps, onScroll: handleScroll}} />;
    };
}

function Select(props: SelectProps): JSX.Element {
    const {
        options,
        value,
        onChange,
        onInputChange,
        placeholder,
        isMenuUsingPortal = false,
        name = '',
        disabled = false,
        components,
        styles,
        dontCloseMenuOnScroll,
        className,
        dataCy,
        onScrollToBottom,
        menuPlacement = 'bottom',
    } = props;

    function isFontName(text: string): boolean {
        return defaultFonts.some((font) => font.name === text);
    }

    const portalContainer = useMemo(() => document.getElementById('portalContainerMain') || document.body, []);

    const onScrollToBottomRef = useRef(onScrollToBottom);

    useEffect(() => {
        onScrollToBottomRef.current = onScrollToBottom;
    }, [onScrollToBottom]);

    const customMenuList = useMemo(() => createCustomMenuList(onScrollToBottomRef), []);

    return (
        <ReSelect
            className={className}
            components={{
                ...components,
                DropdownIndicator: customDropdownIndicator,
                MenuList: customMenuList,
            }}
            name={name}
            value={value || null}
            onChange={(newValue) => onChange && onChange(newValue as Option)}
            onInputChange={(newValue) => onInputChange && onInputChange(newValue)}
            isSearchable={!!onInputChange}
            options={options}
            placeholder={placeholder}
            menuPortalTarget={isMenuUsingPortal ? portalContainer : undefined}
            openMenuOnFocus
            menuPlacement={menuPlacement}
            styles={{
                ...styles,
                indicatorSeparator: () => ({display: 'none'}),
                control: (base) => ({
                    ...base,
                    backgroundColor: '#4949491a',
                    border: 'none',
                    fontSize: '0.825rem',
                    borderRadius: '0.5rem',
                    outline: 'none',
                    minHeight: '30px',
                    height: '30px',
                    boxShadow: 'none',
                    '&:hover': {
                        backgroundColor: '#49494926',
                    },
                    cursor: 'pointer',
                }),
                container: (base) => ({
                    ...base,
                    height: 'fit-content',
                }),
                option: (base, {data, isFocused, isSelected}) => ({
                    ...base,
                    fontSize: '0.875rem',
                    color: '#334155',
                    fontFamily: isFontName(data.label) ? data.label : 'Open Sans, sans-serif',
                    padding: '0.375rem',
                    borderRadius: '0.375rem',
                    backgroundColor: isFocused || isSelected ? 'rgb(243, 244, 246)' : 'transparent',
                }),
                dropdownIndicator: (base) => ({
                    ...base,
                    padding: '0rem 0.625rem',
                    color: '#494949',
                    fontSize: '1rem',
                }),
                indicatorsContainer: (base) => ({
                    ...base,
                    padding: '0rem',
                    margin: '0rem',
                }),
                placeholder: (base) => ({
                    ...base,
                    color: '#545454',
                }),
                input: (base) => ({...base, padding: '0rem', margin: '0rem'}),
                menu: (base) => ({
                    ...base,
                    position: 'absolute',
                    zIndex: getCssVariable('--z-index-dropdown'),
                    backgroundColor: '#ffffff',
                    borderRadius: '0.5rem',
                    boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
                    transition: 'transition: transform 0.2s ease, opacity 0.2s ease;',
                    border: '1px solid rgb(229 231 235)',
                    paddingInline: '0.25rem',
                }),
                menuPortal: (base) => ({...base, zIndex: 10000}),
            }}
            closeMenuOnScroll={(e) => {
                return dontCloseMenuOnScroll ? false : !!(isMenuUsingPortal && e.target !== document.getElementById('react-select-menu'));
            }}
            isDisabled={disabled}
            filterOption={createFilter({ignoreAccents: false})}
            data-cy={dataCy}
        />
    );
}

export default Select;
